import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import client from "../api/client";
import { useToast } from "../contexts/ToastContext";
import { useEffects } from "../contexts/SettingsContext";

export default function EffectsPage() {
  const { addToast } = useToast();
  const { effects, setEffect, setVideoUrl } = useEffects();

  const [settings, setSettings] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState(effects.videoUrl);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await client.get("/admin/settings");
      setSettings(res.data?.data ?? {});
    } catch {
      addToast("error", "Failed to load settings");
    }
  }, [addToast]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    setUrlInput(effects.videoUrl);
  }, [effects.videoUrl]);

  const saveSettings = useCallback(async (updates: Record<string, string>) => {
    setSaving(true);
    try {
      await client.put("/admin/settings", updates);
      setSettings((prev) => ({ ...prev, ...updates }));
      if ("effects_binary_rain" in updates) {
        setEffect("binaryRain", updates.effects_binary_rain === "true");
      }
      if ("effects_surveillance" in updates) {
        setEffect("surveillance", updates.effects_surveillance === "true");
      }
      if ("effects_video_url" in updates) {
        setVideoUrl(updates.effects_video_url);
      }
      addToast("success", "Saqlandi");
    } catch {
      addToast("error", "Saqlashda xatolik");
    } finally {
      setSaving(false);
    }
  }, [addToast, setEffect, setVideoUrl]);

  const handleSaveUrl = () => {
    saveSettings({ effects_video_url: urlInput.trim() });
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("video", file);
      const res = await client.post("/settings/upload-video", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const videoUrl = res.data?.data?.videoUrl;
      if (videoUrl) {
        saveSettings({ effects_video_url: videoUrl });
        addToast("success", "Video yuklandi");
      }
    } catch {
      addToast("error", "Video yuklashda xatolik");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDeleteVideo = async () => {
    try {
      await client.delete("/settings/upload-video");
      saveSettings({ effects_video_url: "" });
      addToast("success", "Video o'chirildi");
    } catch {
      addToast("error", "O'chirishda xatolik");
    }
  };

  const isLocalVideo = effects.videoUrl && effects.videoUrl.startsWith("/");

  return (
    <div className="ds-page max-w-3xl">
      <div className="mb-6 flex items-center gap-3">
        <Link
          to="/admin"
          className="text-ds-text-muted hover:text-ds-text transition-colors"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div>
          <h1 className="text-2xl font-semibold text-ds-text">Visual Effects</h1>
          <p className="mt-1 text-sm text-ds-text-muted">Barcha foydalanuvchilar uchun effektlarni boshqarish</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Toggles */}
        <div className="ds-card p-6">
          <h3 className="text-sm font-semibold text-ds-text mb-4">Effektlar</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-lg border border-ds-border bg-ds-surface/50 p-4">
              <div>
                <p className="text-sm font-medium text-ds-text">Matrix Binary Rain</p>
                <p className="text-xs text-ds-text-muted mt-0.5">Fon orqada tushayotgan binary raqamlar</p>
              </div>
              <button
                onClick={() => saveSettings({ effects_binary_rain: effects.binaryRain ? "false" : "true" })}
                disabled={saving}
                className={`relative h-6 w-11 rounded-full transition-colors ${
                  effects.binaryRain ? "bg-ds-success" : "bg-ds-border"
                }`}
              >
                <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform ${effects.binaryRain ? "translate-x-5" : ""}`} />
              </button>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-ds-border bg-ds-surface/50 p-4">
              <div>
                <p className="text-sm font-medium text-ds-text">Hacker Watch</p>
                <p className="text-xs text-ds-text-muted mt-0.5">CCTV monitoring paneli (YouTube yoki local video)</p>
              </div>
              <button
                onClick={() => saveSettings({ effects_surveillance: effects.surveillance ? "false" : "true" })}
                disabled={saving}
                className={`relative h-6 w-11 rounded-full transition-colors ${
                  effects.surveillance ? "bg-ds-success" : "bg-ds-border"
                }`}
              >
                <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform ${effects.surveillance ? "translate-x-5" : ""}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Video settings */}
        {effects.surveillance && (
          <div className="ds-card p-6 space-y-4">
            <h3 className="text-sm font-semibold text-ds-text">Surveillance Video</h3>

            {/* Current video preview */}
            {effects.videoUrl && (
              <div className="rounded-lg border border-ds-border bg-ds-surface/50 p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-ds-text">Hozirgi video</p>
                  <button onClick={handleDeleteVideo} className="text-xs text-ds-danger hover:underline">
                    O'chirish
                  </button>
                </div>
                <div className="aspect-video overflow-hidden rounded bg-black">
                  {isLocalVideo ? (
                    <video src={effects.videoUrl} className="h-full w-full object-cover" muted />
                  ) : (
                    <iframe src={effects.videoUrl} className="h-full w-full" allow="encrypted-media" />
                  )}
                </div>
              </div>
            )}

            {/* YouTube URL */}
            <div className="rounded-lg border border-ds-border bg-ds-surface/50 p-4">
              <p className="text-sm font-medium text-ds-text mb-2">YouTube Link</p>
              <div className="flex gap-2">
                <input
                  className="ds-input flex-1 font-mono text-xs"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                />
                <button
                  onClick={handleSaveUrl}
                  disabled={saving || urlInput === effects.videoUrl}
                  className="ds-btn-primary text-xs"
                >
                  Save
                </button>
              </div>
            </div>

            {/* File upload */}
            <div className="rounded-lg border border-ds-border bg-ds-surface/50 p-4">
              <p className="text-sm font-medium text-ds-text mb-2">Fayldan video yuklash</p>
              <p className="text-xs text-ds-text-muted mb-3">mp4, webm, ogg — max 100MB</p>
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-ds-border bg-ds-card/50 px-4 py-6 transition-colors hover:border-ds-accent/40">
                <svg className="h-5 w-5 text-ds-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                </svg>
                <span className="text-xs text-ds-text-dim">
                  {uploading ? "Yuklanmoqda..." : "Video tanlash"}
                </span>
                <input
                  type="file"
                  accept="video/mp4,video/webm,video/ogg,video/quicktime"
                  className="hidden"
                  onChange={handleFileUpload}
                  disabled={uploading}
                />
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
